import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Header, RadioProps } from 'semantic-ui-react';
import { BillSearchBy, StornoType } from 'ts-types/api.enums';
import { BillReminderSearchResponse, BillStornoSearchRequest, PatientDto } from 'ts-types/api.types';
import moment from 'moment';
import createDecorator, { Calculation } from 'final-form-calculate';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import Radio from 'components/final-form/Radio';
import Input from 'components/final-form/Input';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import DatePicker from 'components/final-form/DatePicker';
import styled from 'styled-components';
import { FormApi, FormState } from 'final-form';
import { isValueEmpty } from 'utils/validatorUtils';
import { debounce } from 'ts-debounce';
import { searchBills } from 'services/billReminderServices';
import { RouteComponentProps } from 'react-router';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  padding: 1em 1em;

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 12rem;
  }
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }
`;

const StyledHeader = styled(Header)`
  margin-bottom: 1em !important;
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 40px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchGroup = styled.div`
  flex: 1 1 25%;
`;

const SearchField = styled.div`
  margin-bottom: 7px;
`;

const StyledSpan = styled.span`
  font-weight: bold;
  padding-left: 1.5rem;
`;

interface ExtendedBillReminderSearchRequest extends BillStornoSearchRequest {
  recentlyChanged: boolean
}

interface Props extends RouteComponentProps<any>, MessageSourceProps {
  onFilterBills: (bills: Array<BillReminderSearchResponse>) => void
  onBillsLoadingChange: (billsLoading: boolean) => void
  currentPatient?: PatientDto
}

let initialValues: Partial<ExtendedBillReminderSearchRequest> = {
  searchBy: BillSearchBy.BILL_NUMBER,
  stornoType: StornoType.BILL,
  fromDate: moment().format("YYYY-MM-DD"),
  toDate: moment().format("YYYY-MM-DD"),
  recentlyChanged: false
}

class ManualBillReminderSearchView extends Component<Props> {

  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();
  cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  constructor(props: Props) {
    super(props);

    const {radioMap} = this;
    const {currentPatient, getMessage} = props;

    const state: any = this.props.location.state;
    const selectedBillId = (state && state.selectedBill) ? state.selectedBill.id : undefined;

    const searchByValues = Object.values(BillSearchBy);

    const radioDefinitions = generateRadioButtonOptionsFromEnum(searchByValues, "searchBy", getMessage);

    radioDefinitions.forEach((radioDefinition, index) => {
      radioMap.set(searchByValues[index], radioDefinition);
    });

    if (selectedBillId) {
      initialValues.searchBy = BillSearchBy.BILL_NUMBER;
      initialValues.billNumber = selectedBillId;
    } else if (currentPatient) {
      initialValues.searchBy = BillSearchBy.CURRENT_PATIENT;
      initialValues.patientId = currentPatient.id;
      initialValues.billNumber = undefined;
    }
  }

  shouldResetSearchBy = (searchBy: BillSearchBy, params: Array<any>): Partial<any> => {

    const allValues: Partial<any> = params[2];
    const prevValues: Partial<any> = params[3];

    // `Object.entries(prevValues).length !== 0` is here to prevent decorator being called when the form is being initialized
    if (searchBy != prevValues.searchBy && !allValues.recentlyChanged && Object.entries(prevValues).length !== 0) {
      return this.resetSearchBy(searchBy);
    }

    return {
      recentlyChanged: false
    }
  };

  resetSearchBy = (searchBy: BillSearchBy): Partial<BillStornoSearchRequest> => {

    const defaultValues = {
      searchBy: searchBy,
      recentlyChanged: true
    };

    switch (searchBy) {
      case BillSearchBy.BILL_NUMBER: {
        return {
          ...defaultValues,
          fromDate: moment().format("YYYY-MM-DD"),
          toDate: moment().format("YYYY-MM-DD"),
          fromAmount: undefined,
          toAmount: undefined
        }
      }
      case BillSearchBy.CURRENT_PATIENT: {
        return {
          ...defaultValues,
          billNumber: undefined,
          fromDate: moment().format("YYYY-MM-DD"),
          toDate: moment().format("YYYY-MM-DD"),
          fromAmount: undefined,
          toAmount: undefined
        }
      }
      case BillSearchBy.AMOUNT: {
        return {
          ...defaultValues,
          billNumber: undefined,
          fromDate: moment().format("YYYY-MM-DD"),
          toDate: moment().format("YYYY-MM-DD")
        }
      }
      case BillSearchBy.DATE: {
        return {
          ...defaultValues,
          billNumber: undefined,
          fromAmount: undefined,
          toAmount: undefined
        }
      }
      default: {
        return {};
      }
    }
  };

  constructCalculation = (fieldName: string, searchBy?: BillSearchBy): Calculation => {
    if (!searchBy) {
      return {
        field: fieldName,
        updates: (...params) => this.shouldResetSearchBy(params[0], params)
      }
    }

    return {
      field: fieldName,
      updates: (...params) => this.shouldResetSearchBy(searchBy, params)
    }
  };

  searchBy = createDecorator(
      this.constructCalculation("billNumber", BillSearchBy.BILL_NUMBER),
      this.constructCalculation("fromAmount", BillSearchBy.AMOUNT),
      this.constructCalculation("toAmount", BillSearchBy.AMOUNT),
      this.constructCalculation("fromDate", BillSearchBy.DATE),
      this.constructCalculation("toDate", BillSearchBy.DATE),
      this.constructCalculation("searchBy")
  );

  shouldSearchBills = (values: Partial<ExtendedBillReminderSearchRequest>): boolean => {

    const {searchBy, billNumber, fromAmount, toAmount} = values;

    switch (searchBy) {
      case BillSearchBy.BILL_NUMBER: {
        return !isValueEmpty(billNumber);
      }
      case BillSearchBy.AMOUNT: {
        return !isValueEmpty(fromAmount) || !isValueEmpty(toAmount);
      }
      default: {
        return true;
      }
    }
  };

  handleChange = ({values}: FormState, form: FormApi) => {

    const {onBillsLoadingChange} = this.props;

    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    if (this.shouldSearchBills(values)) {
      onBillsLoadingChange(true);
      this.searchBills(values);
    }

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }
  };

  searchBills = debounce((values: Partial<ExtendedBillReminderSearchRequest>) => {

    const {onBillsLoadingChange, onFilterBills} = this.props;

    searchBills(values, this.cancelTokenSource)
        .then(response => {
          onFilterBills(response);
        })
        .finally(() => {
          onBillsLoadingChange(false);
        })
  }, 400);

  render(): React.ReactNode {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            decorators={[this.searchBy]}
            initialValues={initialValues}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {

    const {radioMap, props} = this;
    const {getMessage, currentPatient} = props;

    const dateRadioDefinition = radioMap.get(BillSearchBy.DATE);
    const amountRadioDefinition = radioMap.get(BillSearchBy.AMOUNT);
    const billNumberRadioDefinition = radioMap.get(BillSearchBy.BILL_NUMBER);
    const currentPatientRadioDefinition = radioMap.get(BillSearchBy.CURRENT_PATIENT);

    const disabledBefore = values.fromDate && new Date(values.fromDate);

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <StyledHeader>{getMessage("searchOptions")}</StyledHeader>
          </StyledContainer>
          <StyledContainer>
            <SearchOptionsContainer>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={billNumberRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="billNumber"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("searchBy.BILL_NUMBER")}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      disabled={!currentPatient}
                      radioDefinition={currentPatientRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  {currentPatient &&
                  <StyledSpan>
                    {`${currentPatient.name} ${currentPatient.firstName}`}
                  </StyledSpan>
                  }
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={amountRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel>{getMessage("from")}:</StyledLabel>
                  <Field
                      name="fromAmount"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("fromAmount")}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel>{getMessage("to")}:</StyledLabel>
                  <Field
                      name="toAmount"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("toAmount")}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={dateRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel>
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="fromDate"
                      component={DatePicker}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel>
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="toDate"
                      component={DatePicker}
                      disabledBefore={disabledBefore}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </StyledForm>
    )
  }
}

export default withRouterWorkaround(withMessages(ManualBillReminderSearchView));
